import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { TemplateBlock } from "@/components/content/TemplateBlock";
import { Checklist } from "@/components/content/Checklist";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Layout, Target, Phone, Star, MapPin, Image } from "lucide-react";

const faqs = [
  {
    question: "What's different about a landscaping homepage vs lawn care?",
    answer: "Landscaping homepages emphasize design, transformation, and larger projects. Include portfolio images, project galleries, and highlight design expertise alongside maintenance services."
  },
  {
    question: "Should I show pricing on my landscaping homepage?",
    answer: "For landscaping, show starting prices or project ranges rather than fixed prices. 'Patio installations starting at $X' works better than exact quotes since projects vary widely."
  },
  {
    question: "How many portfolio images should I include?",
    answer: "Feature 4-6 of your best projects on the homepage with links to a full portfolio. Choose diverse projects that show your range: patios, plantings, outdoor living, etc."
  },
  {
    question: "What's the most important thing on a landscaping homepage?",
    answer: "Visual proof of your work. Landscaping is visual—homeowners want to see what you can do. Lead with stunning before/after photos and project galleries."
  }
];

const LandscapingHomepageTemplate = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-templates/landscaping-homepage-template/";
  
  return (
    <>
      <SEO
        title="Landscaping Homepage Template | Website Structure Guide"
        description="Free landscaping homepage template. Proven structure for landscaping company websites with portfolio sections, service areas, and converting CTAs."
        url={pageUrl}
        keywords="landscaping homepage template, landscaping website template, landscape company homepage"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Landscaping Homepage Template"
        pageDescription="Homepage template for landscaping company websites with portfolio and service sections."
        faqs={faqs}
      />

      <TopCTA />

      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Website Template</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Landscaping{" "}
              <span className="text-primary">Homepage Template</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A proven homepage structure for landscaping companies. Showcase your work, 
              build trust, and convert visitors into project inquiries.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-bold">Homepage Section Structure</h2>
            
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Section 1: Hero with Portfolio Image
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Hero Headline Options"
                    content={`Transform Your Outdoor Space – [City]'s Premier Landscaping Company

OR

Custom Landscape Design & Installation in [City]

OR

From Vision to Reality: Professional Landscaping in [County]`}
                  />
                  <TemplateBlock
                    title="Hero Subheadline"
                    content={`Award-winning landscape design, hardscape installation, and outdoor living spaces. Serving [City] homeowners for [X] years.`}
                  />
                  <Checklist
                    title="Hero Must-Haves"
                    items={[
                      "Stunning project photo as background",
                      "Clear CTA: 'Start Your Project' or 'Get Design Consultation'",
                      "Phone number visible",
                      "Design/build messaging"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5 text-primary" />
                    Section 2: Portfolio Showcase
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Portfolio Section Headline"
                    content={`Our Recent Projects

OR

See What's Possible for Your Property`}
                  />
                  <Checklist
                    title="Portfolio Section Elements"
                    items={[
                      "4-6 high-quality project images",
                      "Mix of project types (patios, plantings, outdoor kitchens)",
                      "Before/after comparisons when available",
                      "Link to full portfolio page",
                      "Brief project descriptions"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="h-5 w-5 text-primary" />
                    Section 3: Services Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Services Section Headline"
                    content={`Complete Landscaping Services

OR

Design, Build, Maintain – Full-Service Landscaping`}
                  />
                  <Checklist
                    title="Services to Feature"
                    items={[
                      "Landscape Design",
                      "Hardscape Installation (patios, walkways, retaining walls)",
                      "Planting & Softscaping",
                      "Outdoor Living Spaces",
                      "Landscape Maintenance",
                      "Drainage & Grading"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-primary" />
                    Section 4: Trust Signals
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Trust Section Headline"
                    content={`Why Homeowners Trust [Company Name]

OR

[X] Projects Completed. [X] Happy Clients.`}
                  />
                  <Checklist
                    title="Trust Elements"
                    items={[
                      "Google reviews with star rating",
                      "Houzz, Angi, or other platform badges",
                      "Licensing and insurance info",
                      "Years in business",
                      "Industry certifications",
                      "2-3 detailed testimonials"
                    ]}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Section 5: Service Areas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Service Area Section"
                    content={`Serving [County] and Beyond

Professional landscaping services in:
• [City 1] • [City 2] • [City 3]
• [Affluent Neighborhood 1] • [Affluent Neighborhood 2]`}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Section 6: Consultation CTA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TemplateBlock
                    title="Bottom CTA Section"
                    content={`Ready to Transform Your Property?

Schedule your free design consultation. We'll visit your property, discuss your vision, and provide a detailed proposal.

[Schedule Consultation Button] [Call (XXX) XXX-XXXX Button]`}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <MidPageCTA />

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">DIY vs. Done-For-You</h2>
            <DIYvsDoneForYou
              diyPoints={[
                "Implement this template on any platform",
                "Customize with your portfolio and copy",
                "May take 15-25 hours to do well",
                "Free if you do it yourself"
              ]}
              doneForYouPoints={[
                "Professional design that showcases your work",
                "SEO-optimized from day one",
                "Portfolio galleries and project pages included",
                "$200-300/month with ongoing updates"
              ]}
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Templates"
              links={[
                { to: "/website-templates/landscaping-service-page-template/", label: "Landscaping Service Page Template" },
                { to: "/website-templates/service-area-page-template/", label: "Service Area Page Template" },
                { to: "/website-templates/landscaping-website-seo-checklist/", label: "Landscaping SEO Checklist" },
                { to: "/website-templates/", label: "All Templates" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <BottomCTA />
      <CalendlyEmbed />
    </>
  );
};

export default LandscapingHomepageTemplate;
